import { useState, useEffect } from 'react';
import { Consumer } from '@rails/actioncable';
import { FULFILLED } from 'admin-mojo/helpers/StateNotification';
import type { Limit, Limits, Launcher, SessionLimit, CasinoBanLimit } from './types';

const ts = () => new Date().getTime();

const useLimits = (launcher: Launcher): [boolean, Limit | undefined] => {
  const [, setLimits] = useState<Limits>({});
  const [sessionTimer, setSessionTimer] = useState<NodeJS.Timeout>();
  const [banTimer, setBanTimer] = useState<NodeJS.Timeout>();
  const [achievedLimit, setAchievedLimit] = useState<Limit>();
  const [isCalculated, setIsCalculated] = useState(false);

  const updateAchievedLimit = (type: Limit['type'], checkFn: (limit: Limit) => boolean) => {
    setLimits((prevLimits) => {
      setAchievedLimit((prevAchievedLimit) => {
        if (prevAchievedLimit && prevAchievedLimit.type !== type) return prevAchievedLimit;

        const limit = prevLimits[type];
        return limit && checkFn(limit) ? limit : undefined;
      });
      setIsCalculated(true);
      return prevLimits;
    });
  };

  const scheduleSessionLimitCheck = () => {
    updateAchievedLimit(
      'session',
      (limit) => !!(limit as SessionLimit).achievement_ts && ts() >= (limit as SessionLimit).achievement_ts
    );

    setSessionTimer(setTimeout(scheduleSessionLimitCheck, 1000));
  };

  const scheduleBanLimitCheck = () => {
    updateAchievedLimit(
      'casino_ban',
      (limit) => !(limit as CasinoBanLimit).ends_at_ts || ts() < (limit as CasinoBanLimit).ends_at_ts
    );

    setBanTimer(setTimeout(scheduleBanLimitCheck, 1000));
  };

  const updateBetsAndLossLimitStates = () => {
    const types: Limit['type'][] = ['bets', 'loss'];
    types.forEach((type) => updateAchievedLimit(type, (limit) => limit.achieved));
  };

  const updateLimits = (v: { limits: Limits }) => {
    const newLimits = v.limits || {};
    setLimits(newLimits);

    updateBetsAndLossLimitStates();
  };

  // Wait until the launcher is initialized.
  useEffect(() => {
    if (launcher.state.status !== FULFILLED) return;

    const newLimits = launcher.limits || {};
    setLimits(newLimits);

    if (!sessionTimer) scheduleSessionLimitCheck();
    if (!banTimer) scheduleBanLimitCheck();
    updateBetsAndLossLimitStates();
  }, [launcher.state.status]);

  useEffect(() => {
    const cable = window.cable as Consumer;
    const subscription = cable.subscriptions.create(
      {
        channel: 'LimitsChannel',
      },
      {
        received: updateLimits,
      }
    );

    return () => {
      clearTimeout(sessionTimer);
      clearTimeout(banTimer);
      subscription.unsubscribe();
    };
  }, []);

  return [isCalculated, achievedLimit];
};

export default useLimits;
